import React, { FC, ReactNode } from 'react';
import { Badge } from 'src/components/widgets/badge';
import { Icon } from 'src/components/widgets/icon';
import Stack from 'src/components/widgets/stack/stack';
import { stringFormat, T } from 'src/shared/helpers';
import CreditCardIcon from 'src/shared/material_icons/action/credit_card.svg';
import ScheduleIcon from 'src/shared/material_icons/action/schedule.svg';
import WebAssetIcon from 'src/shared/material_icons/av/web_asset.svg';
import MonetizationOnIcon from 'src/shared/material_icons/editor/monetization_on.svg';
import EditIcon from 'src/shared/material_icons/image/edit.svg';
import ArrowForwardIcon from 'src/shared/material_icons/navigation/arrow_forward.svg';
import SyncIcon from 'src/shared/material_icons/notification/sync.svg';
import StorefrontIcon from 'src/shared/material_icons/places/storefront.svg';
import styled from 'styled-components';
import { TONES } from './switchDialogParts';

/**
 * The three blocks of the Review step: the master → new master overview, the notices the seller must read before
 * proceeding, and what to expect while the switch runs.
 */

/** A titled block of the review: a little more room above it than between the dialog's other blocks. */
const ReviewSection = styled(Stack)`
    margin-top: 8px;
`;

const SectionTitle = styled.h3`
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #6b6b6b;
`;

const SectionHeading = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 16px;
`;

const SectionCount = styled.span`
    font-size: 12px;
    color: #6b6b6b;
`;

/* ---------- Overview ---------- */

const Overview = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-around;
    /* Extra breathing room above the infographic so it does not sit right under the dialog title. */
    margin-top: 16px;
    padding: 12px 24px;
    background-color: #f3f5f7;
    border-radius: 8px;
`;

const OverviewArrow = styled(Icon)`
    width: 72px;
    height: 20px;
    color: ${(props) => props.theme.app.primary};

    svg {
        width: 72px;
        height: 20px;
    }
`;

const ChannelNode = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    min-width: 0;
    max-width: 40%;
    text-align: center;
`;

const ChannelAvatar = styled.div<{ $isNewMaster?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    margin-bottom: 0;
    border-radius: 50%;
    border: 1px solid ${(props) => (props.$isNewMaster ? props.theme.app.primary : props.theme.app.borderColor)};
    background-color: ${(props) => (props.$isNewMaster ? props.theme.app.primary : '#ffffff')};
    color: ${(props) => (props.$isNewMaster ? '#ffffff' : '#78909c')};

    ${Icon}, ${Icon} svg {
        width: 18px;
        height: 18px;
    }
`;

const ChannelName = styled.strong`
    max-width: 100%;
    font-size: 13px;
    font-weight: 600;
    color: ${(props) => props.theme.app.textColor};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ChannelRole = styled.span`
    font-size: 11px;
    color: #6b6b6b;
`;

/** "Role · EUR" when the currency is known, just the role otherwise. */
const withCurrency = (role: string, currencyCode?: string | null) => (currencyCode ? `${role} · ${currencyCode}` : role);

export const SwitchReviewOverview: FC<{
    masterSalesChannelName: string;
    secondarySalesChannelName: string;
    masterCurrencyCode?: string | null;
    secondaryCurrencyCode?: string | null;
}> = ({ masterSalesChannelName, secondarySalesChannelName, masterCurrencyCode, secondaryCurrencyCode }) => (
    <Overview>
        <ChannelNode>
            <ChannelAvatar>
                <Icon>
                    <StorefrontIcon />
                </Icon>
            </ChannelAvatar>
            <ChannelName title={masterSalesChannelName}>{masterSalesChannelName}</ChannelName>
            <ChannelRole>{withCurrency(T._('Current master', 'Admin'), masterCurrencyCode)}</ChannelRole>
        </ChannelNode>
        <OverviewArrow>
            <ArrowForwardIcon />
        </OverviewArrow>
        <ChannelNode>
            <ChannelAvatar $isNewMaster>
                <Icon>
                    <StorefrontIcon />
                </Icon>
            </ChannelAvatar>
            <ChannelName title={secondarySalesChannelName}>{secondarySalesChannelName}</ChannelName>
            {/* Same chip as "Master" in the sales channels list. */}
            <Badge variant='info'>{withCurrency(T._('New master', 'Admin'), secondaryCurrencyCode)}</Badge>
        </ChannelNode>
    </Overview>
);

/* ---------- Before you proceed ---------- */

// Same colours as the warning banner the Shopify plan approval notice used to be.
const NoticeList = styled.div`
    padding: 0 16px;
    background-color: ${(props) => props.theme.app.component.notificationBanner.warning.backgroundColor};
    border-radius: 8px;
`;

const NoticeRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 10px 0;

    & + & {
        border-top: 1px solid rgb(232 101 10 / 20%);
    }
`;

// Same treatment as the NotificationBanner warning icon: a plain dark icon (no coloured circle) on the warning-tinted list.
const NoticeIcon = styled(Icon)`
    flex-shrink: 0;
    margin-top: 2px;
    color: #263238;

    svg {
        width: 16px;
        height: 16px;
    }
`;

const NoticeBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    font-size: 13px;
    line-height: 21px;
    color: ${(props) => props.theme.app.textColor};

    p {
        margin: 0;
    }
`;

const NoticeTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    /* Same size as the NotificationBanner title used on step 2 (Payment method required). */
    font-size: 13px;
    font-weight: 600;
    color: ${(props) => props.theme.app.textColor};
`;

const NoticeTag = styled.span`
    padding: 1px 8px;
    border-radius: 10px;
    background-color: #ffffff;
    font-size: 12px;
    font-weight: 600;
    color: ${(props) => props.theme.app.component.notificationBanner.warning.iconContainerBGColor};
`;

interface Notice {
    key: string;
    icon: ReactNode;
    title: string;
    tag?: string;
    paragraphs: string[];
}

interface SwitchReviewNoticesProps {
    masterSalesChannelName: string;
    secondarySalesChannelName: string;
    masterCurrencyCode?: string | null;
    secondaryCurrencyCode?: string | null;
    /** Both stores are Shopify: the subscription is recreated on the new master and approved on Shopify. */
    requiresShopifyApproval: boolean;
    requiresCustomPlanSubscription: boolean;
    requiresPaymentMethodSetup: boolean;
    hasCurrencyMismatch: boolean;
}

/** Everything the seller has to know about this specific pair of stores, as one list. Renders nothing when there is nothing to review. */
export const SwitchReviewNotices: FC<SwitchReviewNoticesProps> = ({
    masterSalesChannelName,
    secondarySalesChannelName,
    masterCurrencyCode,
    secondaryCurrencyCode,
    requiresShopifyApproval,
    requiresCustomPlanSubscription,
    requiresPaymentMethodSetup,
    hasCurrencyMismatch
}) => {
    const notices: Notice[] = [];

    if (requiresShopifyApproval) {
        notices.push({
            key: 'shopify-approval',
            icon: <SyncIcon />,
            title: T._('Subscription approval required', 'Admin'),
            paragraphs: [
                T._('All Zakeke billing happens on the Master Integration, so that is where your subscription and plan must be active.', 'Admin'),
                stringFormat(
                    T._('With the switch, the subscription on {1} is cancelled and recreated on {0}: same plan, same conditions.', 'Admin'),
                    secondarySalesChannelName,
                    masterSalesChannelName
                ),
                T._("When you click Proceed, you'll be redirected to Shopify to approve the new subscription.", 'Admin')
            ]
        });
    }

    if (requiresCustomPlanSubscription) {
        notices.push({
            key: 'custom-plan',
            icon: <SyncIcon />,
            title: T._('New subscription required', 'Admin'),
            // One paragraph: the second sentence follows on the same line instead of starting a new one.
            paragraphs: [
                `${T._(
                    'Because there are differences between the two integrations, you will need to subscribe to a new Zakeke plan for the new Master Integration.',
                    'Admin'
                )} ${T._('The plan conditions will remain unchanged.', 'Admin')}`
            ]
        });
    }

    if (requiresPaymentMethodSetup) {
        notices.push({
            key: 'payment-method',
            icon: <CreditCardIcon />,
            title: T._('Payment method setup', 'Admin'),
            paragraphs: [T._('Later in the process, you will be asked to set up your payment method again before your products are switched.', 'Admin')]
        });
    }

    if (hasCurrencyMismatch) {
        notices.push({
            key: 'currency',
            icon: <MonetizationOnIcon />,
            title: T._('Currency difference', 'Admin'),
            tag: `${masterCurrencyCode ?? ''} → ${secondaryCurrencyCode ?? ''}`,
            paragraphs: [
                T._(
                    'The sales channel you are switching to uses a different currency ({0}) than your current Master Integration ({1}). If you have set a fixed price for one or more items in your Image and Clipart Gallery, or created pricing rules, please account for the exchange rate difference between the two currencies, as these prices will not be converted automatically.',
                    'Admin'
                )
                    .replace('{0}', secondaryCurrencyCode || '')
                    .replace('{1}', masterCurrencyCode || '')
            ]
        });
    }

    if (notices.length === 0) return null;

    return (
        <ReviewSection $gap='8px'>
            <SectionHeading>
                <SectionTitle>{T._('Before you proceed', 'Admin')}</SectionTitle>
                <SectionCount>
                    {notices.length === 1 ? T._('1 thing to review', 'Admin') : stringFormat(T._('{0} things to review', 'Admin'), notices.length.toString())}
                </SectionCount>
            </SectionHeading>
            <NoticeList>
                {notices.map((notice) => (
                    <NoticeRow key={notice.key}>
                        <NoticeIcon>{notice.icon}</NoticeIcon>
                        <NoticeBody>
                            <NoticeTitle>
                                {notice.title}
                                {notice.tag && <NoticeTag>{notice.tag}</NoticeTag>}
                            </NoticeTitle>
                            {notice.paragraphs.map((paragraph) => (
                                <p key={paragraph}>{paragraph}</p>
                            ))}
                        </NoticeBody>
                    </NoticeRow>
                ))}
            </NoticeList>
        </ReviewSection>
    );
};

/* ---------- During the switch ---------- */

const DuringGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`;

const DuringCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 10px 12px;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 8px;
    font-size: 11px;
    line-height: 15px;
    color: #6b6b6b;
`;

const DuringIcon = styled(Icon)<{ $warning?: boolean }>`
    flex-shrink: 0;
    color: ${(props) => (props.$warning ? TONES.warning.color : '#6b6b6b')};
`;

/** Icon and title on one row, so the icon does not take a line of its own. */
const DuringTitle = styled.strong`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: ${(props) => props.theme.app.textColor};
`;

/** At most two lines, so the three cards stay short; the English texts are written to fit. */
const DuringText = styled.span`
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

export const SwitchReviewDuringTheSwitch: FC = () => (
    <ReviewSection $gap='8px'>
        <SectionTitle>{T._('During the switch', 'Admin')}</SectionTitle>
        <DuringGrid>
            <DuringCard>
                <DuringTitle>
                    <DuringIcon>
                        <ScheduleIcon />
                    </DuringIcon>
                    {T._('Usually a few seconds', 'Admin')}
                </DuringTitle>
                <DuringText>{T._('From a few seconds to a couple of hours for very large product catalogs.', 'Admin')}</DuringText>
            </DuringCard>
            <DuringCard>
                <DuringTitle>
                    <DuringIcon $warning>
                        <EditIcon />
                    </DuringIcon>
                    {T._("Don't edit products", 'Admin')}
                </DuringTitle>
                <DuringText>{T._("Avoid importing, creating or editing products until it's complete.", 'Admin')}</DuringText>
            </DuringCard>
            <DuringCard>
                <DuringTitle>
                    <DuringIcon $warning>
                        <WebAssetIcon />
                    </DuringIcon>
                    {T._('Temporary errors', 'Admin')}
                </DuringTitle>
                <DuringText>{T._('Some products may not work properly during the switch process.', 'Admin')}</DuringText>
            </DuringCard>
        </DuringGrid>
    </ReviewSection>
);
